import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar, Clock, Loader2, AlertCircle } from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface AppointmentFieldSettings {
  show_name?: boolean;
  show_email?: boolean;
  show_phone?: boolean;
  show_date?: boolean;
  show_time?: boolean;
  show_message?: boolean;
}

interface LandingAppointmentSectionProps {
  content: any & {
    appointment_fields?: AppointmentFieldSettings;
  };
  theme: any;
  businessInfo: any;
  location: any;
  workspace: any;
  onTrackBooking?: (appointmentData?: Record<string, any>) => void;
}

// Business hours types
interface Period {
  open: string;
  close: string;
}

interface DayHours {
  isOpen: boolean;
  is24Hours: boolean;
  periods: Period[];
}

type BusinessHours = Record<string, DayHours>;

const WEEK_DAYS = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
];

// Convert GMB API format to internal format
const convertGMBHoursToInternal = (hours: any): BusinessHours | null => {
  if (!hours || typeof hours !== "object") return null;

  const internalFormat: BusinessHours = {};

  // Initialize all days as closed
  WEEK_DAYS.forEach((day) => {
    internalFormat[day] = {
      isOpen: false,
      is24Hours: false,
      periods: [],
    };
  });

  // Handle flat GMB format: {periods: [{openDay: "MONDAY", ...}, ...]}
  if (
    hours.periods &&
    Array.isArray(hours.periods) &&
    hours.periods.length > 0
  ) {
    hours.periods.forEach((period: any) => {
      const day = period.openDay;
      if (day && internalFormat[day]) {
        internalFormat[day].isOpen = true;

        const openHour = period.openTime?.hours || 0;
        const openMinute = period.openTime?.minutes || 0;
        const closeHour = period.closeTime?.hours || 0;
        const closeMinute = period.closeTime?.minutes || 0;

        // Check if this period represents 24 hours (00:00 to 24:00)
        const is24Hour =
          openHour === 0 &&
          openMinute === 0 &&
          closeHour === 24 &&
          closeMinute === 0;

        if (is24Hour) {
          internalFormat[day].is24Hours = true;
        } else {
          // Convert 24:00 to 00:00 for display (if not already handled)
          const displayCloseHour = closeHour === 24 ? 0 : closeHour;

          internalFormat[day].periods.push({
            open: `${String(openHour).padStart(2, "0")}:${String(
              openMinute
            ).padStart(2, "0")}`,
            close: `${String(displayCloseHour).padStart(2, "0")}:${String(
              closeMinute
            ).padStart(2, "0")}`,
          });
        }
      }
    });

    return internalFormat;
  }

  // Handle structured format: {MONDAY: {...}, TUESDAY: {...}}
  let hasAnyData = false;
  WEEK_DAYS.forEach((day) => {
    const dayData = hours[day];
    if (!dayData) return;

    hasAnyData = true;

    // Check if already in internal format (has isOpen property)
    if ("isOpen" in dayData) {
      internalFormat[day] = {
        isOpen: dayData.isOpen || false,
        is24Hours: dayData.is24Hours || dayData.is24 || false,
        periods:
          dayData.isOpen && (!dayData.periods || dayData.periods.length === 0)
            ? [{ open: "09:00", close: "17:00" }]
            : dayData.periods || [],
      };
      return;
    }

    // Handle GMB API format (has periods array with openTime/closeTime objects)
    if (dayData.periods && Array.isArray(dayData.periods)) {
      const hasValidPeriods = dayData.periods.length > 0;

      // Check if this represents 24 hours (00:00 to 24:00)
      const is24Hour =
        hasValidPeriods &&
        dayData.periods.length === 1 &&
        dayData.periods[0].openTime?.hours === 0 &&
        dayData.periods[0].openTime?.minutes === 0 &&
        dayData.periods[0].closeTime?.hours === 24 &&
        dayData.periods[0].closeTime?.minutes === 0;

      internalFormat[day] = {
        isOpen: hasValidPeriods,
        is24Hours: is24Hour,
        periods:
          hasValidPeriods && !is24Hour
            ? dayData.periods.map((period: any) => {
                const openHour = period.openTime?.hours || 0;
                const openMinute = period.openTime?.minutes || 0;
                let closeHour = period.closeTime?.hours || 0;
                const closeMinute = period.closeTime?.minutes || 0;

                // Convert 24:00 to 00:00 for display
                if (closeHour === 24) {
                  closeHour = 0;
                }

                return {
                  open: `${String(openHour).padStart(2, "0")}:${String(
                    openMinute
                  ).padStart(2, "0")}`,
                  close: `${String(closeHour).padStart(2, "0")}:${String(
                    closeMinute
                  ).padStart(2, "0")}`,
                };
              })
            : [],
      };
    }
  });

  return hasAnyData ? internalFormat : null;
};

// Get day name from date string
const getDayNameFromDate = (dateStr: string): string | null => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;
  return WEEK_DAYS[date.getDay()];
};

// Format time for display (HH:MM to h:MM AM/PM)
const formatTimeLabel = (time24: string): string => {
  const [hourStr, minuteStr] = time24.split(":");
  const hour = parseInt(hourStr, 10);
  const minute = minuteStr || "00";
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayHour}:${minute} ${period}`;
};

const LandingAppointmentSection = ({
  content,
  theme,
  businessInfo,
  location,
  workspace,
  onTrackBooking,
}: LandingAppointmentSectionProps) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  // Spam trap: bound to an off-screen input humans never see. Bots autofill
  // every field; the edge function silently discards any submission where
  // this arrives non-empty.
  const [hpWebsite, setHpWebsite] = useState("");

  // Field visibility settings - default all fields to visible
  const fieldSettings: AppointmentFieldSettings = useMemo(() => ({
    show_name: content?.appointment_fields?.show_name !== false,
    show_email: content?.appointment_fields?.show_email !== false,
    show_phone: content?.appointment_fields?.show_phone !== false,
    show_date: content?.appointment_fields?.show_date !== false,
    show_time: content?.appointment_fields?.show_time !== false,
    show_message: content?.appointment_fields?.show_message !== false,
  }), [content?.appointment_fields]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Get subdomain from URL
      const hostname = window.location.hostname;
      const subdomain = hostname.split(".")[0];

      // Call the edge function to send appointment notification
      const { data, error } = await supabase.functions.invoke(
        "send-appointment-notification",
        {
          body: {
            locationId: location.id,
            workspaceId: workspace.id,
            subdomain: subdomain,
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            date: formData.date,
            time: formData.time,
            message: formData.message,
            website: hpWebsite,
          },
        }
      );

      if (error) {
        console.error("Error submitting appointment:", error);
        throw new Error(error.message || "Failed to submit appointment");
      }

      if (!data?.success) {
        throw new Error(data?.error || "Failed to submit appointment");
      }

      toast.success(
        "Appointment request sent! We'll contact you to confirm your booking."
      );

      // Track appointment booking conversion
      if (onTrackBooking) {
        onTrackBooking({
          date: formData.date,
          time: formData.time,
          name: formData.name,
        });
      }

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        date: "",
        time: "",
        message: "",
      });
    } catch (error: any) {
      console.error("Error submitting appointment:", error);
      toast.error(
        error.message ||
          "Failed to submit appointment. Please try again or contact us directly."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Today's date in YYYY-MM-DD, used as `min=` on the date <input>. Computed
  // in an effect (not during render) because SSR runs on Vercel in UTC and
  // the browser hydrates in the visitor's local timezone — a same-day render
  // between the two can differ by one calendar date, which shows up as a
  // hydration mismatch on the input's `min` attribute (FLY-LOCAL-BOOST-B).
  // Empty string on first paint is fine: the browser's date picker just has
  // no lower bound for a single React tick before the effect fills it in.
  const [today, setToday] = useState("");
  useEffect(() => {
    setToday(new Date().toISOString().split("T")[0]);
  }, []);

  // Parse business hours from props
  const parsedBusinessHours = useMemo(() => {
    const hours =
      businessInfo?.hours ||
      location?.regular_hours ||
      location?.business_hours;
    return convertGMBHoursToInternal(hours);
  }, [businessInfo?.hours, location?.regular_hours, location?.business_hours]);

  // Get the selected day's name and hours
  const selectedDayName = useMemo(
    () => getDayNameFromDate(formData.date),
    [formData.date]
  );

  const selectedDayHours = useMemo(() => {
    if (!selectedDayName || !parsedBusinessHours) return null;
    return parsedBusinessHours[selectedDayName];
  }, [selectedDayName, parsedBusinessHours]);

  // Check if the selected day is closed
  const isSelectedDayClosed = useMemo(() => {
    if (!formData.date) return false;
    if (!parsedBusinessHours) return false; // No hours data, use default slots
    if (!selectedDayHours) return true;
    return !selectedDayHours.isOpen;
  }, [formData.date, parsedBusinessHours, selectedDayHours]);

  // Generate time slots based on business hours for selected day
  const generateTimeSlots = (
    dayHours: DayHours | null,
    hasBusinessHours: boolean
  ): { value: string; label: string }[] => {
    const slots: { value: string; label: string }[] = [];

    // If no business hours data at all, use default 9-5
    if (!hasBusinessHours) {
      for (let hour = 9; hour <= 17; hour++) {
        slots.push({
          value: `${hour.toString().padStart(2, "0")}:00`,
          label: formatTimeLabel(`${hour.toString().padStart(2, "0")}:00`),
        });
        if (hour < 17) {
          slots.push({
            value: `${hour.toString().padStart(2, "0")}:30`,
            label: formatTimeLabel(`${hour.toString().padStart(2, "0")}:30`),
          });
        }
      }
      return slots;
    }

    // If day is closed or no hours data for this day, return empty
    if (!dayHours || !dayHours.isOpen) {
      return [];
    }

    // If 24 hours, generate full day slots
    if (dayHours.is24Hours) {
      for (let hour = 0; hour < 24; hour++) {
        slots.push({
          value: `${hour.toString().padStart(2, "0")}:00`,
          label: formatTimeLabel(`${hour.toString().padStart(2, "0")}:00`),
        });
        slots.push({
          value: `${hour.toString().padStart(2, "0")}:30`,
          label: formatTimeLabel(`${hour.toString().padStart(2, "0")}:30`),
        });
      }
      return slots;
    }

    // Generate slots for each period
    dayHours.periods.forEach((period) => {
      const [openHour, openMinute] = period.open.split(":").map(Number);
      let [closeHour, closeMinute] = period.close.split(":").map(Number);

      // Handle midnight closing (00:00 means end of day)
      if (closeHour === 0 && closeMinute === 0) {
        closeHour = 24;
      }

      // Generate 30-minute slots within this period
      let currentHour = openHour;
      let currentMinute = openMinute;

      while (
        currentHour < closeHour ||
        (currentHour === closeHour && currentMinute < closeMinute)
      ) {
        const timeValue = `${currentHour
          .toString()
          .padStart(2, "0")}:${currentMinute.toString().padStart(2, "0")}`;
        slots.push({
          value: timeValue,
          label: formatTimeLabel(timeValue),
        });

        // Increment by 30 minutes
        currentMinute += 30;
        if (currentMinute >= 60) {
          currentMinute = 0;
          currentHour += 1;
        }
      }
    });

    // Sort slots by time
    slots.sort((a, b) => a.value.localeCompare(b.value));

    // Remove duplicates (in case of overlapping periods)
    const uniqueSlots = slots.filter(
      (slot, index, arr) => index === 0 || slot.value !== arr[index - 1].value
    );

    return uniqueSlots;
  };

  const timeSlots = useMemo(() => {
    return generateTimeSlots(selectedDayHours, !!parsedBusinessHours);
  }, [selectedDayHours, parsedBusinessHours]);

  // Reset time selection when date changes and the selected time is no longer valid
  useEffect(() => {
    if (formData.time && timeSlots.length > 0) {
      const isTimeValid = timeSlots.some(
        (slot) => slot.value === formData.time
      );
      if (!isTimeValid) {
        setFormData((prev) => ({ ...prev, time: "" }));
      }
    }
  }, [formData.date, timeSlots]);

  // Get display name for the day
  const getDisplayDayName = (dayName: string | null): string => {
    if (!dayName) return "";
    return dayName.charAt(0) + dayName.slice(1).toLowerCase();
  };

  return (
    <section id="appointment" className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="section-heading mb-4">
            {content?.heading || "Ready to Get Started?"}
          </h2>
          <div className="section-divider" />
          <p className="section-description mt-6">
            {content?.description ||
              "Pick a time that works for you, and we'll take care of the rest. Quick, easy, and no hassle."}
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="modern-card p-8 border-0">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Spam trap: off-screen, tabindex -1; humans never touch it. */}
              <div style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }} aria-hidden="true">
                <label>
                  Website
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" value={hpWebsite} onChange={(e) => setHpWebsite(e.target.value)} />
                </label>
              </div>
              {/* Name and Email row */}
              {(fieldSettings.show_name || fieldSettings.show_email) && (
                <div className="grid md:grid-cols-2 gap-6">
                  {fieldSettings.show_name && (
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Full Name {(!fieldSettings.show_phone) ? '*' : ''}
                      </label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required={!fieldSettings.show_phone}
                        placeholder="John Doe"
                      />
                    </div>
                  )}
                  {fieldSettings.show_email && (
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Email Address
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Phone field */}
              {fieldSettings.show_phone && (
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Phone Number {(!fieldSettings.show_name) ? '*' : '*'}
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="(555) 123-4567"
                  />
                </div>
              )}

              {/* Date and Time row */}
              {(fieldSettings.show_date || fieldSettings.show_time) && (
                <div className="grid md:grid-cols-2 gap-6">
                  {fieldSettings.show_date && (
                    <div>
                      <label
                        htmlFor="date"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        <Calendar className="h-4 w-4 inline mr-1" />
                        Preferred Date *
                      </label>
                      <Input
                        id="date"
                        name="date"
                        type="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                        min={today}
                      />
                    </div>
                  )}
                  {fieldSettings.show_time && (
                    <div>
                      <label
                        htmlFor="time"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        <Clock className="h-4 w-4 inline mr-1" />
                        Preferred Time {fieldSettings.show_date ? '*' : ''}
                      </label>
                      {isSelectedDayClosed && fieldSettings.show_date ? (
                        <div className="flex items-center gap-2 h-10 px-3 py-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md">
                          <AlertCircle className="h-4 w-4 flex-shrink-0" />
                          <span>
                            Closed on {getDisplayDayName(selectedDayName)}
                          </span>
                        </div>
                      ) : (
                        <select
                          id="time"
                          name="time"
                          value={formData.time}
                          onChange={handleChange}
                          required={fieldSettings.show_date}
                          disabled={
                            fieldSettings.show_date &&
                            parsedBusinessHours &&
                            (!formData.date || timeSlots.length === 0)
                          }
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <option value="">
                            {fieldSettings.show_date && parsedBusinessHours && !formData.date
                              ? "Select a date first"
                              : "Select a time"}
                          </option>
                          {timeSlots.map((slot) => (
                            <option key={slot.value} value={slot.value}>
                              {slot.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Message/Notes field */}
              {fieldSettings.show_message && (
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Additional Notes
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Any specific requirements or questions..."
                  />
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={
                  loading || (fieldSettings.show_date && parsedBusinessHours && isSelectedDayClosed)
                }
                style={{
                  backgroundColor: "var(--primary)",
                  color: "var(--primary-foreground)",
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : fieldSettings.show_date && parsedBusinessHours && isSelectedDayClosed ? (
                  "Please select an open day"
                ) : (
                  "Request Appointment"
                )}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default LandingAppointmentSection;
